import { createSlice } from '@reduxjs/toolkit'
import {
  addPdf,
  resetPdf,
  initializePdfPages,
  replacePdf,
  backFillPagesAndLayouts,
} from 'apps/studio/store/actions/sharedActions'
import { loadFormData } from 'apps/formsCreationKit/store/actions/sharedActions'
import { saveApp } from 'apps/formsCreationKit/store/reducers/settings/appSettings'
import { createDefaultLayoutData } from 'apps/studio/lib/utils/entities'
import {
  createElement,
  updateElement,
  deleteElement,
  createTable,
  moveTable,
  resizeTable,
  deleteTable,
  addTableColumn,
  deleteTableColumn,
  addTableRow,
  deleteTableRow,
} from 'apps/studio/store/actions/sharedActions'

// const layoutDefaults = {
//   id: null,
//   parent_layout_id: null,
//   section_id: null,
//   page_id: null,
//   layout_type: "absolute",
//   position: 0,
//   styles: {}
// }

export const initialState = []

// Merge a partial layout ({id, position, columns?|rows?}) into the matching layout
const patchLayout = (state, { payload: { layout } }) =>
  layout ? state.map(l => (l.id === layout.id ? { ...l, ...layout } : l)) : state

export const layoutsSliceExtraReducers = builder => {
  builder
    .addCase(loadFormData, (state, action) => {
      const { layouts = [] } = action.payload
      return layouts
    })
    .addCase(createElement, (state, action) => {
      const { section, layout } = action.payload

      return section && layout
        ? state.map(l =>
            l.id === layout.id ? { ...l, section_id: section.id } : l
          )
        : state
    })
    .addCase(createTable, (state, action) => {
      const { layout } = action.payload
      return [...state, layout]
    })
    .addCase(deleteTable, (state, action) => {
      const { layout } = action.payload
      if (!layout?.id) return state
      return state.filter(l => l.id !== layout.id)
    })
    .addCase(resizeTable, (state, action) => {
      const { layout } = action.payload
      if (!layout) return state
      return state.map(l =>
        l.id === layout.id
          ? {
              ...l,
              position: layout.position,
              columns: layout.columns,
              rows: layout.rows,
            }
          : l
      )
    })
    .addCase(moveTable, (state, action) => {
      const { layout } = action.payload
      if (!layout) return state
      return state.map(l => (l.id === layout.id ? layout : l))
    })
    .addCase(addTableColumn, patchLayout)
    .addCase(deleteTableColumn, patchLayout)
    .addCase(addTableRow, patchLayout)
    .addCase(deleteTableRow, patchLayout)
    .addCase(updateElement, (state, action) => {
      const { sectionToDelete, section, layout } = action.payload

      return state.reduce((acc, l) => {
        let updated = l
        if (section && layout && l.id === layout.id) {
          updated = { ...updated, section_id: section.id }
        }
        if (sectionToDelete && l.section_id === sectionToDelete) {
          updated = { ...updated, section_id: null }
        }
        acc.push(updated)
        return acc
      }, [])
    })
    .addCase(deleteElement, (state, action) => {
      const { section, layout } = action.payload

      if (section.id && layout.id) {
        return state.map(l =>
          l.id === layout.id ? { ...l, section_id: null } : l
        )
      }

      return state
    })
    .addCase(addPdf, () => [])
    .addCase(initializePdfPages, (state, action) => {
      const { pageCount, pageGuids } = action.payload
      return Array.from({ length: pageCount }, (_, i) =>
        createDefaultLayoutData(pageGuids[i])
      )
    })
    .addCase(backFillPagesAndLayouts, (state, action) => {
      const { missingPageNumbers, pageGuids } = action.payload
      const missingLayouts = Array.from(
        { length: missingPageNumbers.length },
        (_, i) => createDefaultLayoutData(pageGuids[i])
      )
      return [...state, ...missingLayouts]
    })
    .addCase(resetPdf, (state, action) =>
      // May need to revisit this but since we are only resetting the PDF to it's original state,
      // we need to keep only the top level parent layouts and then wipe any section associations and styles
      state.reduce((acc, layout) => {
        if (!layout.parent_layout_id) {
          acc.push({ ...layout, section_id: null, styles: {} })
        }
        return acc
      }, [])
    )
    .addCase(replacePdf, (state, action) => {
      const { removedPageIds } = action.payload
      return state.filter(layout => !removedPageIds.includes(layout.page_id))
    })
    .addCase(saveApp.fulfilled, (state, action) => {
      const { pdf } = action.payload
      const { layouts } = pdf
      return layouts
    })
}

export const layoutsSliceConfig = {
  name: 'layouts',
  initialState: initialState,
  reducers: {},
  extraReducers: layoutsSliceExtraReducers,
}

export const layoutsSlice = createSlice(layoutsSliceConfig)
export const { actions: layoutsActions, reducer: layoutsReducer } = layoutsSlice
